import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe,
  Shield,
  Clock,
  ChevronRight,
  ArrowRight,
  Mail,
} from "lucide-react";
import axios from "axios";
import ParticleCanvas from "@/components/ParticleCanvas";
import BookingModal from "@/components/BookingModal";
import { services } from "@/data/services";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, delay, ease: "easeOut" },
});

const heroHeadline = ["Energetische", "Heilung."];
const heroSub = ["Tiefe", "Transformation."];

export default function Home() {
  const [bookingService, setBookingService] = useState(null);

  const handleServiceClick = (service) => {
    if (service.bookingType === "modal") {
      setBookingService(service);
    }
  };

  return (
    <div className="bg-[#050510] text-[#F0EDE8]">
      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <ParticleCanvas density={1} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050510]/30 via-transparent to-[#050510]" />

        <div className="relative z-10 text-center max-w-5xl mx-auto px-6 pt-24">
          <motion.p
            className="text-[0.7rem] uppercase tracking-[0.5em] text-[#C9A84C] mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Remote-Sessions · Weltweit verfügbar
          </motion.p>

          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-8"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {heroHeadline.map((word, i) => (
              <motion.span
                key={`a${i}`}
                className={`inline-block mr-4 ${
                  i === 0 ? "text-[#C9A84C]" : "text-[#F0EDE8]"
                }`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 + i * 0.12, ease: "easeOut" }}
              >
                {word}
              </motion.span>
            ))}
            <br />
            {heroSub.map((word, i) => (
              <motion.span
                key={`b${i}`}
                className="inline-block mr-4 text-[#F0EDE8]"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.78 + i * 0.12, ease: "easeOut" }}
              >
                {word}
              </motion.span>
            ))}
          </h1>

          <motion.p
            className="text-[#9A9A9A] text-lg md:text-xl font-light mb-12 tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            Remote-Sessions · Weltweit verfügbar · Auralia
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
          >
            <Link
              to="/leistungen"
              className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
              data-testid="hero-leistungen-btn"
            >
              Leistungen entdecken
            </Link>
            <Link
              to="/leistungen"
              className="flex items-center gap-2 text-[#F0EDE8] text-xs uppercase tracking-[0.2em] px-8 py-4 hover:text-[#C9A84C] transition-colors"
              data-testid="hero-buchen-btn"
            >
              Direkt buchen <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <div className="w-px h-14 bg-gradient-to-b from-transparent to-[#C9A84C]/50 mx-auto" />
        </motion.div>
      </section>

      {/* ── TRUST STRIP ── */}
      <motion.section
        className="border-y border-[#1A1A2E]"
        {...fadeUp(0)}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-8">
          {[
            { icon: Globe, text: "Remote verfügbar" },
            { icon: Shield, text: "Persönlich & vertraulich" },
            { icon: Clock, text: "Sofortige Terminbestätigung" },
          ].map(({ icon: Icon, text }) => (
            <div
              key={text}
              className="flex items-center gap-3 text-[#9A9A9A] text-xs uppercase tracking-[0.22em]"
            >
              <Icon size={14} className="text-[#C9A84C]" />
              {text}
            </div>
          ))}
        </div>
      </motion.section>

      {/* ── SERVICES TEASER ── */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" {...fadeUp()}>
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Meine Leistungen
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-[#F0EDE8]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Energetische Heilung auf allen Ebenen
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((service, i) => (
            <motion.div
              key={service.id}
              className="relative bg-[#0D0D1A] border border-[#1A1A2E] p-8 rounded-2xl hover:border-[#9B72CF]/40 transition-all duration-500 group cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => handleServiceClick(service)}
              data-testid={`home-service-${service.id}`}
            >
              {/* Inner glow on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                style={{ background: "radial-gradient(circle at 50% 0%, rgba(155,114,207,0.06), transparent 70%)" }}
              />

              {service.badge && (
                <span className="absolute top-4 right-4 text-[0.6rem] uppercase tracking-[0.18em] text-[#050510] bg-[#C9A84C] px-3 py-1">
                  {service.badge}
                </span>
              )}

              <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#C9A84C] mb-4">
                {service.categoryLabel}
              </p>
              <h3
                className="text-xl font-light text-[#F0EDE8] mb-3 leading-snug"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {service.name}
              </h3>
              <p className="text-[#9A9A9A] text-sm leading-relaxed mb-6">
                {service.shortDescription}
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[0.6rem] uppercase tracking-[0.15em] text-[#9A9A9A]">ab </span>
                  <span
                    className="text-[#C9A84C] text-xl font-light"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {service.startingFrom}
                  </span>
                </div>
                <ChevronRight
                  size={16}
                  className="text-[#9A9A9A] group-hover:text-[#C9A84C] transition-colors"
                />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div className="text-center mt-14" {...fadeUp()}>
          <Link
            to="/leistungen"
            className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
            data-testid="all-services-btn"
          >
            Alle Leistungen ansehen
          </Link>
        </motion.div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="relative py-24 md:py-32 border-y border-[#1A1A2E] overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <ParticleCanvas density={0.3} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-16" {...fadeUp()}>
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
              Ablauf
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-[#F0EDE8]"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Wie funktioniert eine Session?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                num: "01",
                title: "Termin buchen",
                desc: "Wähle deine gewünschte Leistung und buche deinen Termin direkt online — einfach und unkompliziert.",
              },
              {
                num: "02",
                title: "Vorbereitungsfragebogen",
                desc: "Du erhältst vorab einen kurzen Fragebogen, damit ich mich optimal auf deine Situation vorbereiten kann.",
              },
              {
                num: "03",
                title: "Remote-Session",
                desc: "Die Session findet bequem per Telefon oder Video statt — von überall auf der Welt.",
              },
            ].map(({ num, title, desc }, i) => (
              <motion.div
                key={num}
                className="text-center"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.15 }}
              >
                <div
                  className="text-6xl text-[#C9A84C]/20 font-light mb-5"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {num}
                </div>
                <h3
                  className="text-2xl font-light text-[#F0EDE8] mb-4"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {title}
                </h3>
                <p className="text-[#9A9A9A] text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" {...fadeUp()}>
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Erfahrungen
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-[#F0EDE8]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Was Klienten sagen
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              name: "Maria K.",
              service: "Energetische Wirbelsäulenaufrichtung",
              text: "Nach der Session fühlte ich mich wie neu geboren. Eine unglaubliche Leichtigkeit hat sich eingestellt, die ich so vorher nicht kannte.",
            },
            {
              name: "Thomas B.",
              service: "Reading im morphischen Feld",
              text: "Das Reading hat mir so viel Klarheit über meine aktuelle Situation gebracht. Ich bin beeindruckt von der Tiefe und Präzision.",
            },
            {
              name: "Sandra M.",
              service: "Tierkommunikation",
              text: "Endlich verstehe ich meinen Hund viel besser. Die Session war zutiefst bewegend und hat unsere Bindung enorm gestärkt.",
            },
          ].map(({ name, service, text }, i) => (
            <motion.div
              key={name}
              className="bg-[#0D0D1A] border border-[#1A1A2E] p-8 rounded-2xl"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, j) => (
                  <span key={j} className="text-[#C9A84C]">★</span>
                ))}
              </div>
              <p className="text-[#9A9A9A] text-sm leading-relaxed mb-6 italic">
                "{text}"
              </p>
              <div>
                <p className="text-[#F0EDE8] text-sm font-light">{name}</p>
                <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[#C9A84C] mt-1">
                  {service}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="relative py-24 md:py-32 overflow-hidden border-t border-[#1A1A2E]">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <ParticleCanvas density={0.6} />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-6">
              Dein nächster Schritt
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-[#F0EDE8] mb-8"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Bereit für deine Transformation?
            </h2>
            <p className="text-[#9A9A9A] text-lg mb-10">
              Erlebe tiefe Heilung und nachhaltige Veränderung — bequem von zu Hause aus.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/leistungen"
                className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
                data-testid="cta-buchen-btn"
              >
                Jetzt buchen
              </Link>
              <Link
                to="/kontakt"
                className="text-[#F0EDE8] text-xs uppercase tracking-[0.2em] px-8 py-4 hover:text-[#C9A84C] transition-colors"
                data-testid="cta-kontakt-btn"
              >
                Kontakt aufnehmen
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <NewsletterSection />

      {bookingService && (
        <BookingModal
          service={bookingService}
          onClose={() => setBookingService(null)}
        />
      )}
    </div>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      setStatus({ ok: true, msg: res.data.message });
    } catch {
      setStatus({ ok: false, msg: "Ein Fehler ist aufgetreten. Bitte versuche es erneut." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border-t border-[#1A1A2E] py-20">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Mail size={20} className="text-[#C9A84C] mx-auto mb-5" />
          <h2
            className="text-3xl md:text-4xl font-light text-[#F0EDE8] mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Newsletter
          </h2>
          <p className="text-[#9A9A9A] text-sm mb-8">
            Impulse, Einblicke und exklusive Angebote direkt in dein Postfach.
          </p>
          {status ? (
            <p className={`text-sm ${status.ok ? "text-[#C9A84C]" : "text-red-400"}`}>
              {status.msg}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@email.de"
                className="flex-1 bg-[#0D0D1A] border border-[#1A1A2E] text-[#F0EDE8] px-5 py-3 text-sm placeholder-[#9A9A9A]/40 focus:border-[#C9A84C] focus:outline-none transition-colors"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.18em] px-8 py-3 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300 disabled:opacity-40"
                data-testid="newsletter-submit-btn"
              >
                {loading ? "..." : "Anmelden"}
              </button>
            </form>
          )}
          <p className="text-[#9A9A9A] text-xs mt-4">Kein Spam. Jederzeit abmeldbar.</p>
        </motion.div>
      </div>
    </section>
  );
}
